import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../context/DarkModeContext';
import { createLogger } from '../../utils/logger';

const logger = createLogger({ module: 'TourOverlay' });

export interface TourStep {
  element?: string;
  title: string;
  content: string;
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right';
}

interface TourOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  steps: TourStep[];
  onComplete?: () => void;
}

const TourOverlay: React.FC<TourOverlayProps> = ({ isOpen, onClose, steps, onComplete }) => {
  const { darkMode } = useDarkMode();
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' });

  const currentStepData = steps[currentStep];

  // Update spotlight and tooltip position when step changes
  const updatePositions = useCallback(() => {
    if (!currentStepData.element) {
      setSpotlightRect(null);
      setTooltipPosition({ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }

    const element = document.querySelector(currentStepData.element);
    if (!element) {
      logger.warn(`Tour element not found: ${currentStepData.element}`);
      return;
    }

    // Get the element's bounding rect
    const rect = element.getBoundingClientRect();
    
    // Get computed styles to check for transforms
    const computedStyle = window.getComputedStyle(element);
    const parentElement = element.parentElement;
    const parentRect = parentElement ? parentElement.getBoundingClientRect() : null;
    
    // Log for debugging
    logger.debug(`Highlighting element: ${currentStepData.element}`, {
      selector: currentStepData.element,
      element: element,
      rect: rect,
      parentRect: parentRect,
      elementClasses: element.className,
      elementTag: element.tagName,
      transform: computedStyle.transform,
      position: computedStyle.position,
      top: computedStyle.top,
      scrollY: window.scrollY
    });
    
    setSpotlightRect(rect); // Use viewport-relative rect for spotlight

    // Calculate tooltip position based on the position hint
    const margin = 20;
    const tooltipWidth = 400; // Approximate width
    const tooltipHeight = 220; // Increased height
    let newPosition: any = {};

    switch (currentStepData.position) {
      case 'bottom':
        newPosition = {
          left: `${rect.left + rect.width / 2}px`,
          top: `${rect.bottom + margin}px`,
          transform: 'translateX(-50%)'
        };
        break;
      case 'top':
        newPosition = {
          left: `${rect.left + rect.width / 2}px`,
          top: `${rect.top - tooltipHeight - margin}px`,
          transform: 'translateX(-50%)'
        };
        break;
      case 'left':
        newPosition = {
          left: `${rect.left - tooltipWidth - margin}px`,
          top: `${rect.top + rect.height / 2}px`,
          transform: 'translateY(-50%)'
        };
        break;
      case 'right':
        newPosition = {
          left: `${rect.right + margin}px`,
          top: `${rect.top + rect.height / 2}px`,
          transform: 'translateY(-50%)'
        };
        break;
      default:
        newPosition = {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        };
    }

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    if (newPosition.left && newPosition.left !== '50%') {
      const leftNum = parseInt(newPosition.left);
      if (leftNum < margin) newPosition.left = `${margin}px`;
      if (leftNum + tooltipWidth > viewportWidth - margin) {
        newPosition.left = `${viewportWidth - tooltipWidth - margin}px`;
      }
    }
    
    if (newPosition.top && newPosition.top !== '50%') {
      const topNum = parseInt(newPosition.top);
      if (topNum < margin) newPosition.top = `${margin}px`;
      if (topNum + tooltipHeight > viewportHeight - margin) {
        newPosition.top = `${viewportHeight - tooltipHeight - margin}px`;
      }
    }

    setTooltipPosition(newPosition);
  }, [currentStepData]);

  useEffect(() => {
    if (isOpen) {
      // Force a reflow to ensure DOM is ready
      document.body.offsetHeight;
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Scroll element into view if needed
        if (currentStepData.element) {
          const element = document.querySelector(currentStepData.element);
          if (element) {
            // For sticky elements, we need to ensure they're visible
            const rect = element.getBoundingClientRect();
            if (rect.top < 0 || rect.bottom > window.innerHeight) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            
            // Update positions after any animations/transitions
            setTimeout(() => {
              updatePositions();
            }, 300);
          } else {
            logger.error(`Element not found for tour step: ${currentStepData.element}`);
            updatePositions();
          }
        } else {
          updatePositions();
        }
      }, 50);
    }
  }, [isOpen, currentStep, currentStepData.element, updatePositions]);

  // Listen for window resize and scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => {
      updatePositions();
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate);
    };
  }, [isOpen, updatePositions]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    onComplete?.();
    onClose();
    setCurrentStep(0);
  };

  const handleSkip = () => {
    onClose();
    setCurrentStep(0);
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Dark overlay with cutout for spotlight */}
          <svg
            className="fixed inset-0 z-[9998]"
            style={{ width: '100%', height: '100%' }}
          >
            <defs>
              <mask id="spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {spotlightRect && (
                  <rect
                    x={spotlightRect.left - 8}
                    y={spotlightRect.top - 8}
                    width={spotlightRect.width + 16}
                    height={spotlightRect.height + 16}
                    rx="8"
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0, 0, 0, 0.75)"
              mask="url(#spotlight-mask)"
              onClick={handleSkip}
              style={{ cursor: 'pointer' }}
            />
          </svg>

          {/* Highlight border around element */}
          {spotlightRect && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed pointer-events-none z-[9999]"
              style={{
                left: spotlightRect.left - 8 + 'px',
                top: spotlightRect.top - 8 + 'px',
                width: spotlightRect.width + 16 + 'px',
                height: spotlightRect.height + 16 + 'px',
                border: '3px solid #3b82f6',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.5), 0 0 40px rgba(59, 130, 246, 0.3)'
              }}
            />
          )}

          {/* Tooltip - independent fixed element */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className={`fixed max-w-md rounded-xl p-6 shadow-2xl z-[10000] ${
              darkMode ? 'bg-gray-800 border border-blue-500' : 'bg-white border border-blue-500'
            }`}
            style={tooltipPosition}
          >
          <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            {currentStepData.title}
          </h3>
          <p className={`text-sm leading-relaxed mb-5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {currentStepData.content}
          </p>

          {/* Progress and Navigation */}
          <div className="flex items-center justify-between">
            {/* Progress dots */}
            <div className="flex gap-1.5">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'bg-blue-500'
                      : darkMode
                      ? 'bg-gray-600'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleSkip}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  darkMode
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-gray-600 hover:text-gray-700'
                }`}
              >
                Skip
              </button>
              
              {currentStep > 0 && (
                <button
                  onClick={handlePrev}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  Previous
                </button>
              )}
              
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="px-3 py-1.5 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleFinish}
                  className="px-3 py-1.5 text-sm bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
                >
                  Finish Tour
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default TourOverlay;